import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {
  CELL_COLLAPSED,
  CELL_KEY_SEP,
  CELL_WILDCARD,
  isWildcardToken,
  PropertyCharacteristic,
  RateCell
} from "../../../../model/rate/RateConfig";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {resolveCellForExactValues} from "../../../../model/rate/RateConfigEvaluator";
import {PaperTypeService} from "../../../service-data/paper-type.service";
import {PackageTypeService} from "../../../service-data/package-type.service";
import {FacilityService} from "../../../service-data/facility.service";
import {describeCharacteristicValue} from "../../../util/rate-characteristic-labels";

/**
 * Header cell rendered into the column-header `<thead>` rows OR the
 * left side of each body row. `value` is the literal axis token (or
 * `'*'` for the wildcard row/col). `span` is the colspan / rowspan
 * value: an OUTER axis header spans all the inner-axis combinations
 * underneath it.
 */
interface HeaderCell {
  value: string;
  /** Display label (after wildcard relabelling). */
  label: string;
  /** colspan (column headers) / rowspan (row headers). 1 for innermost level. */
  span: number;
  /** Position of this header's axis within rowAxes / colAxes. */
  axisLevel: number;
  /** Which propertyCharacteristic this header belongs to. Used by the
   *  template to resolve `paperType` / `packageType` / `carrierServiceLevel`
   *  ids to their human-friendly description for display + tooltip. */
  characteristic?: PropertyCharacteristic;
  /** Extended description for the matTooltip (e.g. PackageType dimensions
   *  + carrier). Empty when the raw token is already self-describing. */
  tooltip?: string;
}

/**
 * Renders the cell grid for a {@link PropertyCharacteristicGroup} as a
 * single Google-Sheets-style pivot table.
 *
 * Each {@link PropertyCharacteristic} in the group is assigned to either
 * the ROW axis or the COLUMN axis (parent {@link GroupBuilderComponent}
 * supplies the assignment via `rowAxes` / `colAxes`). Multiple
 * characteristics on the same axis nest hierarchically: the FIRST entry
 * is the OUTERMOST nesting level, with subsequent entries nesting
 * inside. Outer-axis header cells use `colspan` (column headers) or
 * `rowspan` (row headers) so that one outer label visually owns all of
 * its inner-axis combinations.
 *
 * The cell key for a (rowPath, colPath) coordinate is composed by
 * placing each axis's value at the position dictated by the original
 * `characteristics[]` order — independent of how the user has chosen
 * to pivot. This keeps storage stable across pivot reshuffles.
 *
 * Per-axis values:
 *  - Every literal value from {@link axes} (e.g. paperType IDs).
 *  - `*` (CELL_WILDCARD) always — the generic wildcard fallback slot.
 *  - `-` (CELL_COLLAPSED) shown when either at least one authored cell
 *    uses `-` at that storage position, OR the user has clicked the
 *    per-characteristic "show — slot" toggle in the matrix corner.
 *
 * A `-` slot at axis position i sits alongside the literal values and
 * `*`. Cells in the `-` slot are authored normally (one cell per
 * combination of OTHER axis values), so the user can express things
 * like `GRAY|-` (specific interiorType, "—" quantityBreak) as a single
 * editable cell. Multiple axes can carry `-` slots simultaneously; a
 * cell's key may contain `-` at any subset of its positions as long
 * as at least one position carries a literal or `*` (a key of all
 * `-`'s is functionally identical to the all-`*` fallback, so we don't
 * forbid it but the editor does not auto-create one).
 */
@Component({
  selector: 'app-rate-group-matrix',
  templateUrl: './group-matrix.component.html',
  styleUrls: ['./group-matrix.component.css']
})
export class GroupMatrixComponent implements OnChanges, OnInit {
  @Input() characteristics: PropertyCharacteristic[] = [];
  @Input() axes: { characteristic: PropertyCharacteristic; values: string[] }[] = [];
  @Input() cells: { [k: string]: RateCell } = {};
  @Input() readOnly: boolean = false;

  /** User's pivot choice, outermost first. Sanitized and defaulted as needed. */
  @Input() rowAxes: PropertyCharacteristic[] = [];
  @Input() colAxes: PropertyCharacteristic[] = [];

  /**
   * When true, the Rows / Columns drag-drop bins above the table are
   * hidden. The matrix table itself is always rendered. Used by the
   * GroupBuilder's "Hide setup" toggle to declutter once the user is
   * done arranging axes.
   */
  @Input() hideLayoutBins: boolean = false;

  @Output() cellAmountChange = new EventEmitter<{ key: string; amount: number }>();
  @Output() cellInfeasibleToggle = new EventEmitter<string>();
  /** Emitted when the user repivots (drag-drop chip or "swap axes"). */
  @Output() axesLayoutChange = new EventEmitter<{
    rowAxes: PropertyCharacteristic[];
    colAxes: PropertyCharacteristic[];
  }>();

  readonly localizeNumber = (n: any) => LanguagePipe.localizeNumber(n);
  readonly delocalizeNumber = (s: string) => LanguagePipe.delocalizeNumber(s);

  /** Effective row-axis assignment after defaulting / sanitization. */
  effectiveRowAxes: PropertyCharacteristic[] = [];
  /** Effective col-axis assignment after defaulting / sanitization. */
  effectiveColAxes: PropertyCharacteristic[] = [];

  /**
   * UI-only state: for each `characteristics[i]`, whether the `-`
   * (collapsed) slot is currently visible on that axis. The slot is
   * shown automatically when any authored cell uses `-` at position
   * `i`; the user can also force-show it by clicking the per-
   * characteristic toggle in the matrix corner (so they can author a
   * fresh `-` cell without first authoring a cell key by hand).
   *
   * Not persisted to the document — when no `-` cell exists and the
   * user navigates away, the toggle resets. That's fine: no
   * information is lost; re-clicking the toggle restores the slot.
   */
  private showCollapsedSlotUser: { [c in PropertyCharacteristic]?: boolean } = {};

  /**
   * Per-axis value lists (in row / col axis order). The wildcard
   * `'*'` is appended to each axis so existing wildcard-keyed cells
   * render as a row / col labelled "Any".
   */
  rowAxisValues: string[][] = [];
  colAxisValues: string[][] = [];

  /** Cartesian of `rowAxisValues`. One value-tuple per body row. */
  rowPaths: string[][] = [];
  /** Cartesian of `colAxisValues`. One value-tuple per body column. */
  colPaths: string[][] = [];

  /**
   * Shadow patterns derived from authored cells whose key contains `-`
   * at one or more positions AND that carry a value (numeric `amount`
   * or `infeasible: true`). An empty cell (`{}`) does NOT shadow.
   *
   * Each pattern is a token array: positions holding `'-'` are the
   * "shadowing" positions, all other positions hold the literal /
   * `'*'` token from the authored key. A target cell-key is "shadowed"
   * when, for some pattern P:
   *   - the target's token at every NON-`-` position of P equals P's
   *     token at that position; AND
   *   - the target's token at every `-` position of P is NOT `'-'`
   *     (i.e. the target is a different — non-collapsed — sibling).
   *
   * The shadow cell itself is the only valued cell allowed to express
   * that combination, so the editor hides its non-`-` siblings entirely
   * (no input, no infeasible button, no row/col labels affected).
   */
  private shadowPatterns: string[][] = [];

  /**
   * Column-header rows (one per column-axis level). Outer-level cells
   * use `span` as colspan; the innermost level emits one cell per
   * `colPaths` entry.
   */
  colHeaderRows: HeaderCell[][] = [];
  /**
   * For each body row, the row-header cells to emit BEFORE the cell
   * inputs. A row "owns" an outer-axis cell only when it is the FIRST
   * row in that cell's rowspan group; subsequent rows omit the cell
   * entirely (rowspan on the prior row covers it).
   */
  rowHeaderCells: HeaderCell[][] = [];

  /** drag-drop bin ids — must be unique per component instance. */
  readonly rowBinId = 'rgm-row-bin-' + Math.random().toString(36).slice(2);
  readonly colBinId = 'rgm-col-bin-' + Math.random().toString(36).slice(2);

  constructor(public paperTypeService: PaperTypeService,
              public packageTypeService: PackageTypeService,
              public facilityService: FacilityService) {}

  ngOnInit(): void {
    // PaperType / PackageType / Facility catalogs feed the rich
    // axis-header labels (PackageType name + carrier + service +
    // dimensions; Facility friendly name + city/state). If the
    // catalogs aren't loaded yet, fetch them on demand and recompute
    // the matrix once they arrive so the headers refresh from raw ids
    // to friendly labels without requiring the user to click anything.
    // Cheap when already loaded — the guard returns immediately.
    if (!this.paperTypeService.objects || this.paperTypeService.objects.length === 0) {
      this.paperTypeService.getItems().subscribe(() => this.recompute());
    }
    if (!this.packageTypeService.objects || this.packageTypeService.objects.length === 0) {
      this.packageTypeService.getItems().subscribe(() => this.recompute());
    }
    if (!this.facilityService.objects || this.facilityService.objects.length === 0) {
      this.facilityService.getItems().subscribe(() => this.recompute());
    }
  }

  ngOnChanges(_: SimpleChanges): void { this.recompute(); }

  // ── Layout / pivot ──────────────────────────────────────────────

  private recompute(): void {
    const chars = (this.characteristics || []).slice();
    if (chars.length === 0) {
      this.effectiveRowAxes = [];
      this.effectiveColAxes = [];
      this.rowAxisValues = [];
      this.colAxisValues = [];
      this.rowPaths = [];
      this.colPaths = [];
      this.colHeaderRows = [];
      this.rowHeaderCells = [];
      return;
    }

    // Sanitize the user's pivot choice: drop tokens not in characteristics,
    // suppress duplicates between rowAxes and colAxes (rowAxes wins), and
    // append any uncovered characteristics. Default split when both axes
    // arrive empty: rows get the first ceil(N/2), cols get the rest.
    const charSet = new Set<PropertyCharacteristic>(chars);
    const r0 = (this.rowAxes || []).filter(c => charSet.has(c));
    const r0Set = new Set<PropertyCharacteristic>(r0);
    const c0 = (this.colAxes || []).filter(c => charSet.has(c) && !r0Set.has(c));
    const claimed = new Set<PropertyCharacteristic>([...r0, ...c0]);
    const unclaimed = chars.filter(c => !claimed.has(c));
    if (r0.length === 0 && c0.length === 0) {
      const half = Math.ceil(chars.length / 2);
      this.effectiveRowAxes = chars.slice(0, half);
      this.effectiveColAxes = chars.slice(half);
    } else {
      this.effectiveRowAxes = r0.slice();
      this.effectiveColAxes = c0.slice();
      for (const c of unclaimed) {
        if (this.effectiveRowAxes.length <= this.effectiveColAxes.length) {
          this.effectiveRowAxes.push(c);
        } else {
          this.effectiveColAxes.push(c);
        }
      }
    }

    // Per-axis value lists.
    //
    // Each axis surfaces:
    //   - every literal value from `axes` (e.g. paperType IDs);
    //   - `*` (CELL_WILDCARD) always — the generic wildcard slot;
    //   - `-` (CELL_COLLAPSED) when EITHER at least one authored cell
    //     uses `-` at this storage position OR the user has clicked the
    //     per-characteristic "show — slot" toggle in the matrix corner.
    //
    // The `-` slot sits alongside the literals + `*` so the user can
    // author cells that are independent of one specific axis (e.g.
    // `GRAY|-` = specific interiorType, "—" quantityBreak) without
    // losing the differentiated cells already authored at the literal
    // slots.
    const valuesByChar: { [c: string]: string[] } = {};
    const tokensByPos: Set<string>[] = chars.map(() => new Set<string>());
    for (const k of Object.keys(this.cells || {})) {
      const tk = k.split(CELL_KEY_SEP);
      for (let i = 0; i < tk.length && i < tokensByPos.length; i++) { tokensByPos[i].add(tk[i]); }
    }
    for (let i = 0; i < chars.length; i++) {
      const ax = (this.axes || []).find(a => a.characteristic === chars[i]);
      const literals = (ax && ax.values) ? [...ax.values] : [];
      const tail: string[] = [CELL_WILDCARD];
      const userWants = !!this.showCollapsedSlotUser[chars[i]];
      if (tokensByPos[i].has(CELL_COLLAPSED) || userWants) { tail.push(CELL_COLLAPSED); }
      valuesByChar[chars[i]] = [...literals, ...tail];
    }
    this.rowAxisValues = this.effectiveRowAxes.map(c => valuesByChar[c] || [CELL_WILDCARD]);
    this.colAxisValues = this.effectiveColAxes.map(c => valuesByChar[c] || [CELL_WILDCARD]);

    this.rowPaths = cartesian(this.rowAxisValues);
    this.colPaths = cartesian(this.colAxisValues);
    // Resolve each axis token to (label, tooltip) using the shared
    // describeCharacteristicValue() helper so paperType / packageType
    // IDs and carrierServiceLevel / facility codes show their human-
    // friendly descriptions in the matrix axis headers (with the rich
    // tooltip on hover). The header builders accept a per-axis
    // "describe" callback so the wildcard / collapsed sentinels can
    // short-circuit to the existing relabelling rule ('*' → "Any",
    // '-' → "—").
    const describe = (c: PropertyCharacteristic, raw: string) => {
      if (raw === CELL_WILDCARD) { return {label: LanguagePipe.get('Any', 'compressSpaces'), tooltip: ''}; }
      if (raw === CELL_COLLAPSED) { return {label: '—', tooltip: ''}; }
      const d = describeCharacteristicValue(c, raw, this.paperTypeService, this.packageTypeService, this.facilityService);
      return {label: d.label || raw, tooltip: d.tooltip};
    };
    this.colHeaderRows = buildColHeaderRows(this.colAxisValues, this.effectiveColAxes, describe);
    this.rowHeaderCells = buildRowHeaderCells(this.rowAxisValues, this.effectiveRowAxes, describe);

    // Build shadow patterns from authored cells whose key contains `-`
    // at one or more positions AND that carry a value. Empty cells are
    // not authoritative and never shadow their siblings.
    this.shadowPatterns = [];
    for (const k of Object.keys(this.cells || {})) {
      const cell = this.cells[k];
      const hasValue = cell != null && (
        cell.infeasible === true ||
        (typeof cell.amount === 'number' && isFinite(cell.amount))
      );
      if (!hasValue) { continue; }
      const tk = k.split(CELL_KEY_SEP);
      if (tk.length !== this.characteristics.length) { continue; }
      if (tk.indexOf(CELL_COLLAPSED) < 0) { continue; }
      this.shadowPatterns.push(tk);
    }
  }

  // ── Cell access ─────────────────────────────────────────────────

  /**
   * Compose the storage cell-key for a (rowPath, colPath) coordinate.
   * Values are placed at positions dictated by `characteristics[]`
   * (storage order), which may differ from the user's pivot order.
   * That keeps storage stable across pivot reshuffles.
   */
  cellKey(rowPath: string[], colPath: string[]): string {
    const tokens: string[] = new Array(this.characteristics.length).fill(CELL_WILDCARD);
    for (let i = 0; i < this.effectiveRowAxes.length; i++) {
      const pos = this.characteristics.indexOf(this.effectiveRowAxes[i]);
      if (pos >= 0) { tokens[pos] = rowPath[i]; }
    }
    for (let i = 0; i < this.effectiveColAxes.length; i++) {
      const pos = this.characteristics.indexOf(this.effectiveColAxes[i]);
      if (pos >= 0) { tokens[pos] = colPath[i]; }
    }
    return tokens.join(CELL_KEY_SEP);
  }

  cellAt(rowPath: string[], colPath: string[]): RateCell | undefined {
    return this.cells[this.cellKey(rowPath, colPath)];
  }

  /**
   * Display string for a cell — empty when the cell is missing, empty,
   * or infeasible (the icon column communicates infeasibility).
   *
   * For cells whose composed key contains `-` at one or more positions
   * (i.e. "collapsed-slot" cells), an unauthored cell is rendered with
   * an EMPTY string instead of a default `0`. Those cells are gated
   * behind an explicit "enable" button in the template — the user must
   * click it before an editable input appears. This makes the
   * difference between "no value here" and "authored 0" unambiguous,
   * and also lets the user author a literal `0` (which the engine
   * treats as a valid shadowing value).
   *
   * For non-collapsed cells the legacy default of `0` is preserved so
   * the existing matrix UX is unchanged.
   */
  displayAmount(cell: RateCell | undefined, isCollapsedKey: boolean = false): string {
    if (cell && cell.infeasible) { return ''; }
    if (!cell || typeof cell.amount !== 'number' || !isFinite(cell.amount)) {
      if (isCollapsedKey) { return ''; }
      return this.localizeNumber(0);
    }
    return this.localizeNumber(cell.amount);
  }

  isInfeasible(cell: RateCell | undefined): boolean {
    return cell != null && cell.infeasible === true;
  }

  // ── Engine-truthful display for unauthored cells ────────────────
  //
  // The legacy matrix rendered `0` for every unauthored non-`-` cell.
  // That misled the user when a wider wildcard (e.g. `*: infeasible`)
  // would actually apply at runtime — the cell looked like it would
  // contribute 0, but the engine resolved it to "infeasible" and the
  // Rates Preview reflected that, producing a confusing mismatch.
  //
  // The matrix now consults the same resolver the engine uses and
  // surfaces the INHERITED value (or infeasibility) for every
  // unauthored cell. The cell still LOOKS unauthored (lighter colour,
  // italicized hint, no input box yet) but the value displayed is what
  // the engine would actually use. Clicking on it materializes a real
  // authored cell so the user can override the inherited value.

  /**
   * Token tuple at storage positions for the (rowPath, colPath) coord.
   * Mirrors {@link cellKey} but returns the array form so we don't have
   * to re-split. Tokens may be literal axis values, `'*'`, or `'-'`.
   */
  private exactTokensFor(rowPath: string[], colPath: string[]): string[] {
    const tokens: string[] = new Array(this.characteristics.length).fill(CELL_WILDCARD);
    for (let i = 0; i < this.effectiveRowAxes.length; i++) {
      const pos = this.characteristics.indexOf(this.effectiveRowAxes[i]);
      if (pos >= 0) { tokens[pos] = rowPath[i]; }
    }
    for (let i = 0; i < this.effectiveColAxes.length; i++) {
      const pos = this.characteristics.indexOf(this.effectiveColAxes[i]);
      if (pos >= 0) { tokens[pos] = colPath[i]; }
    }
    return tokens;
  }

  /**
   * Run the engine's resolver against the matrix's local `cells` for
   * the given coordinate. Returns the matched cell + whether the match
   * came from a different (wider-wildcard) key than the coordinate's
   * exact key. The matrix uses this to render the "inherited" value
   * (or infeasibility) so unauthored cells don't lie to the user.
   */
  private resolvedFor(rowPath: string[], colPath: string[]):
      { cell: RateCell | undefined; inherited: boolean } {
    const tokens = this.exactTokensFor(rowPath, colPath);
    const out = resolveCellForExactValues(this.cells, tokens);
    return {cell: out.cell, inherited: out.inherited};
  }

  /**
   * Cell-effectively-infeasible: true when the authored cell at this
   * coordinate is infeasible, OR — when no exact cell is authored —
   * the engine would resolve to an infeasible wider-wildcard cell.
   * Drives the `∞` rendering and the red infeasible-icon background.
   */
  isEffectivelyInfeasible(rowPath: string[], colPath: string[]): boolean {
    const exactCell = this.cellAt(rowPath, colPath);
    if (exactCell && (exactCell.infeasible === true ||
        (typeof exactCell.amount === 'number' && isFinite(exactCell.amount)))) {
      return exactCell.infeasible === true;
    }
    // Unauthored: ask the engine what would resolve here.
    const resolved = this.resolvedFor(rowPath, colPath).cell;
    return !!(resolved && resolved.infeasible === true);
  }

  /**
   * Best display string for the cell — the authored amount when
   * present, otherwise the value the engine would resolve to via a
   * wider wildcard / `-` sibling. Empty when nothing resolves
   * (engine would return no-match = 0 contribution, but we render the
   * cell empty to make it clear nothing's authored at any level).
   *
   * The `inherited` half of the return tells the template whether to
   * style the value as a faint "inherited" hint (italics, lighter
   * colour) versus a solid authored amount.
   */
  effectiveDisplay(rowPath: string[], colPath: string[]):
      { text: string; inherited: boolean; infeasible: boolean } {
    const exactCell = this.cellAt(rowPath, colPath);
    if (exactCell) {
      if (exactCell.infeasible === true) {
        return {text: '', inherited: false, infeasible: true};
      }
      if (typeof exactCell.amount === 'number' && isFinite(exactCell.amount)) {
        return {text: this.localizeNumber(exactCell.amount), inherited: false, infeasible: false};
      }
    }
    // Unauthored — consult the engine.
    const {cell, inherited} = this.resolvedFor(rowPath, colPath);
    if (cell && cell.infeasible === true) {
      return {text: '∞', inherited: true, infeasible: true};
    }
    if (cell && typeof cell.amount === 'number' && isFinite(cell.amount)) {
      return {text: this.localizeNumber(cell.amount), inherited: true, infeasible: false};
    }
    // Nothing resolves at any level — show 0 (the engine's no-match
    // contribution) but mark it inherited so the user can tell no
    // cell actually holds this value.
    return {text: this.localizeNumber(0), inherited: true, infeasible: false};
  }

  /** True when the cell at (rowPath, colPath) has been explicitly authored. */
  isAuthoredExact(rowPath: string[], colPath: string[]): boolean {
    const c = this.cellAt(rowPath, colPath);
    if (!c) { return false; }
    if (c.infeasible === true) { return true; }
    return typeof c.amount === 'number' && isFinite(c.amount);
  }

  /**
   * True when the composed cell-key for (rowPath, colPath) carries `-`
   * (CELL_COLLAPSED) at one or more storage positions. These cells
   * declare an equivalence class spanning all literal/`*` values at
   * the `-` positions, so they shadow their siblings (see
   * {@link isShadowed}). Editing them is gated behind an explicit
   * "enable" button in the template — see {@link isAuthored} /
   * {@link enableCollapsedCell}.
   */
  isCollapsedKey(rowPath: string[], colPath: string[]): boolean {
    const key = this.cellKey(rowPath, colPath);
    const tokens = key.split(CELL_KEY_SEP);
    for (const t of tokens) { if (t === CELL_COLLAPSED) { return true; } }
    return false;
  }

  /**
   * True when the cell at (rowPath, colPath) is currently authored —
   * i.e. it exists in {@link cells} with either `infeasible: true` or
   * a finite numeric `amount`. Unauthored `-` cells are shown as just
   * an "enable" button instead of a `0`-prefilled input, so the user
   * can deliberately bring the cell into existence (and can author a
   * literal `0` without it being confused with the default).
   */
  isAuthored(rowPath: string[], colPath: string[]): boolean {
    const cell = this.cellAt(rowPath, colPath);
    if (!cell) { return false; }
    if (cell.infeasible === true) { return true; }
    return typeof cell.amount === 'number' && isFinite(cell.amount);
  }

  /**
   * "Enable" a `-` (collapsed-key) cell — seeds it with `amount: 0`
   * so the editable input + infeasible toggle appear. The user can
   * then type the real value (or click infeasible) in place. Doing
   * this also activates the cell's shadowing effect on its non-`-`
   * siblings.
   */
  enableCollapsedCell(rowPath: string[], colPath: string[]): void {
    if (this.readOnly) { return; }
    const key = this.cellKey(rowPath, colPath);
    this.cellAmountChange.emit({key, amount: 0});
  }

  /**
   * "Disable" an authored `-` (collapsed-key) cell — deletes it so
   * the slot reverts to the enable button and the previously-
   * shadowed siblings become editable again. Implemented by emitting
   * `NaN` (parent's `setCellAmount` treats non-finite as "delete").
   */
  disableCollapsedCell(rowPath: string[], colPath: string[]): void {
    if (this.readOnly) { return; }
    const key = this.cellKey(rowPath, colPath);
    this.cellAmountChange.emit({key, amount: NaN});
  }

  /**
   * Materialize an unauthored non-`-` cell so the user can edit it in
   * place. The override pencil's intent is "I want to author a value
   * different from what's inherited", so:
   *
   *  - If the inherited resolution is infeasible (e.g. `*: infeasible`
   *    is the wider match), seed the cell with `{amount: 0}` — the
   *    user clearly wants to make this combo feasible. Without this
   *    seed they'd land on `{infeasible: true}` and the only way out
   *    would be to delete the cell (going back to inheriting `∞`),
   *    with no path to an editable amount.
   *
   *  - If the inherited resolution carries a numeric amount, seed
   *    with that same amount so the input appears prefilled with the
   *    value they were already seeing.
   *
   *  - If nothing resolves, seed with `0`.
   *
   * The user can always toggle the freshly-authored cell to infeasible
   * via the in-cell check/block button if that's what they wanted.
   */
  materializeInherited(rowPath: string[], colPath: string[]): void {
    if (this.readOnly) { return; }
    if (this.isAuthoredExact(rowPath, colPath)) { return; }
    const key = this.cellKey(rowPath, colPath);
    const {cell} = this.resolvedFor(rowPath, colPath);
    // Always materialize as a feasible authored cell with a numeric
    // amount. The override pencil exists precisely to break out of
    // an inherited value — landing the user on an authored
    // infeasible cell would just reproduce the inherited state and
    // give them no editable input. If they want infeasible they can
    // either leave the cell unauthored (still inherits `∞` if that's
    // what `*` says) or click the in-cell block toggle after editing.
    const seedAmount = (cell && !cell.infeasible
        && typeof cell.amount === 'number' && isFinite(cell.amount))
      ? cell.amount : 0;
    this.cellAmountChange.emit({key, amount: seedAmount});
  }

  /**
   * True when ANY token in the row-path or col-path is the collapsed
   * sentinel `-`. The template uses this only to apply a subtle
   * background tint (`.rgm-cell-collapsed`) so the user can see at a
   * glance that the cell came from a collapsed-axis row or column.
   * Editing the amount and toggling infeasibility are still allowed —
   * a `-` token means "this axis position doesn't differentiate at
   * this group level", not "this cell is locked".
   */
  isCollapsedPath(rowPath: string[], colPath: string[]): boolean {
    for (const t of rowPath) { if (t === CELL_COLLAPSED) { return true; } }
    for (const t of colPath) { if (t === CELL_COLLAPSED) { return true; } }
    return false;
  }

  /**
   * True when the cell at (rowPath, colPath) is shadowed by some OTHER
   * authored `-` cell — i.e. there is a valued cell in `cells` whose
   * key has `-` at one or more positions and matches this cell's key
   * at every other (non-`-`) position, AND this cell itself does NOT
   * carry `-` at those same positions.
   *
   * Shadowed cells are hidden by the template: a `-` cell at position
   * `i` claims the whole slice along position `i` (for the fixed
   * other-position tokens), so its literal / `*` siblings cannot also
   * carry differentiated values. The shadow cell itself is the only
   * valued cell for that slice and remains editable.
   */
  isShadowed(rowPath: string[], colPath: string[]): boolean {
    if (this.shadowPatterns.length === 0) { return false; }
    const key = this.cellKey(rowPath, colPath).split(CELL_KEY_SEP);
    for (const pattern of this.shadowPatterns) {
      let match = true;
      let differsAtCollapsedPos = false;
      for (let i = 0; i < pattern.length; i++) {
        if (pattern[i] === CELL_COLLAPSED) {
          // Target must NOT also be `-` here — that would be the
          // shadow cell itself (or an equivalent shadow), not a
          // shadowed sibling.
          if (key[i] !== CELL_COLLAPSED) { differsAtCollapsedPos = true; }
        } else if (pattern[i] !== key[i]) {
          match = false;
          break;
        }
      }
      if (match && differsAtCollapsedPos) { return true; }
    }
    return false;
  }

  /**
   * Handle cell-input change. An empty input string is intentionally
   * passed through as `NaN` — the parent {@link GroupBuilderComponent}
   * interprets a non-finite amount as "delete this cell" so the user
   * can clear a value to make it fall back to a wider-wildcard cell.
   */
  onAmountInput(rowPath: string[], colPath: string[], raw: string): void {
    if (this.readOnly) { return; }
    const key = this.cellKey(rowPath, colPath);
    const trimmed = (raw == null ? '' : ('' + raw).trim());
    if (trimmed === '') { this.cellAmountChange.emit({key, amount: NaN}); return; }
    const n = this.delocalizeNumber(trimmed);
    this.cellAmountChange.emit({key, amount: n});
  }

  /**
   * Click handler for the in-cell check/block toggle.
   *
   * The semantics depend on whether the cell is currently authored at
   * its exact key:
   *
   *  - **Authored** (any amount or infeasible flag stored at this key) →
   *    forward the parent's toggle behavior: feasible↔infeasible cycle,
   *    or delete on toggling-off infeasible.
   *  - **Unauthored but inheriting infeasible** (e.g. a wider
   *    `*: infeasible` makes this cell effectively `∞`) → clicking the
   *    block icon is the user's way of saying "make this combo
   *    feasible", so we materialize an authored `{amount: 0}` cell
   *    instead of authoring another `{infeasible: true}` (which would
   *    look identical and trap the user — see the earlier bug report).
   *  - **Unauthored and inheriting a finite amount** → the icon is the
   *    feasible check; clicking it means "mark this combo infeasible"
   *    via the parent's existing toggle.
   */
  onInfeasibleClick(rowPath: string[], colPath: string[]): void {
    if (this.readOnly) { return; }
    const key = this.cellKey(rowPath, colPath);
    if (!this.isAuthoredExact(rowPath, colPath) && this.isEffectivelyInfeasible(rowPath, colPath)) {
      // Materialize a feasible authored cell at amount 0 so the user
      // immediately lands on an editable input.
      this.cellAmountChange.emit({key, amount: 0});
      return;
    }
    this.cellInfeasibleToggle.emit(key);
  }

  /** Friendly label for a cell-key token — `'*'` becomes "Any", `'-'` becomes "—". */
  labelFor(value: string): string {
    if (value === CELL_WILDCARD) { return LanguagePipe.get('Any', 'compressSpaces'); }
    if (value === CELL_COLLAPSED) { return '—'; }
    return value;
  }

  /**
   * Friendly label for a {@link HeaderCell} — uses the precomputed
   * description-aware label if present (e.g. PackageType name instead
   * of bare id), otherwise falls back to the wildcard relabelling
   * of {@link labelFor}. The matTooltip on the `<th>` carries the
   * extended description (carrier, service level, dimensions).
   */
  labelForHeader(h: HeaderCell): string {
    if (h && h.label) { return h.label; }
    return this.labelFor(h ? h.value : '');
  }

  /** True when this header value is a wildcard sentinel (`*` or `-`). */
  isWildcardHeader(value: string): boolean { return isWildcardToken(value); }

  // ── Per-characteristic "show — slot" toggle ─────────────────────

  /**
   * True when this characteristic currently has its `-` (collapsed)
   * slot visible — either because an authored cell already uses `-`
   * at this storage position or because the user clicked the
   * per-characteristic toggle in the matrix corner.
   *
   * UI-only; never persisted. See {@link showCollapsedSlotUser}.
   */
  hasCollapsedSlot(c: PropertyCharacteristic): boolean {
    if (this.showCollapsedSlotUser[c]) { return true; }
    const idx = this.characteristics.indexOf(c);
    if (idx < 0) { return false; }
    for (const k of Object.keys(this.cells || {})) {
      const tk = k.split(CELL_KEY_SEP);
      if (tk[idx] === CELL_COLLAPSED) { return true; }
    }
    return false;
  }

  /**
   * True when the `-` slot for this characteristic can be hidden
   * (i.e. the user-only toggle is on AND no authored cell uses `-`
   * at this storage position). When false, the toggle button is
   * shown but clicking again will be a no-op since at least one
   * authored cell already pins the slot open.
   */
  canHideCollapsedSlot(c: PropertyCharacteristic): boolean {
    if (!this.showCollapsedSlotUser[c]) { return false; }
    const idx = this.characteristics.indexOf(c);
    if (idx < 0) { return false; }
    for (const k of Object.keys(this.cells || {})) {
      const tk = k.split(CELL_KEY_SEP);
      if (tk[idx] === CELL_COLLAPSED) { return false; }
    }
    return true;
  }

  /** Material icon for the per-characteristic "show — slot" toggle. */
  characteristicCollapseIcon(c: PropertyCharacteristic): string {
    return this.hasCollapsedSlot(c) ? 'remove_circle_outline' : 'add_circle_outline';
  }

  /** Tooltip for the per-characteristic "show — slot" toggle. */
  characteristicCollapseTooltip(c: PropertyCharacteristic): string {
    if (this.hasCollapsedSlot(c)) {
      if (this.canHideCollapsedSlot(c)) {
        return LanguagePipe.get('Hide the — slot on this characteristic', 'compressSpaces');
      }
      return LanguagePipe.get('— slot pinned by authored cells; remove all — cells at this position to hide it', 'compressSpaces');
    }
    return LanguagePipe.get('Show a — slot on this characteristic so cells can opt out of differentiating by it', 'compressSpaces');
  }

  /**
   * Toggle the per-characteristic "show — slot" flag. The flag is
   * UI-only — when off and no authored cell uses `-` at this storage
   * position, the `-` slot disappears from the matrix. When at least
   * one authored cell uses `-`, the slot stays visible regardless of
   * this flag (so the cells remain editable).
   */
  toggleCharacteristicCollapseSlot(c: PropertyCharacteristic): void {
    if (this.readOnly) { return; }
    const idx = this.characteristics.indexOf(c);
    if (idx < 0) { return; }
    if (this.hasCollapsedSlot(c)) {
      if (this.canHideCollapsedSlot(c)) {
        this.showCollapsedSlotUser[c] = false;
        this.recompute();
      }
      // Else: pinned by authored cells; do nothing (tooltip explains).
      return;
    }
    this.showCollapsedSlotUser[c] = true;
    this.recompute();
  }

  /** Friendly label for a propertyCharacteristic. */
  characteristicLabel(c: PropertyCharacteristic): string {
    switch (c) {
      case 'bindingType':         return LanguagePipe.get('Binding type', 'compressSpaces');
      case 'interiorType':        return LanguagePipe.get('Interior type', 'compressSpaces');
      case 'laminateType':        return LanguagePipe.get('Laminate type', 'compressSpaces');
      case 'paperType':           return LanguagePipe.get('Paper type', 'compressSpaces');
      case 'packageType':         return LanguagePipe.get('Package type', 'compressSpaces');
      case 'carrierServiceLevel': return LanguagePipe.get('Carrier service level', 'compressSpaces');
      case 'facility':            return LanguagePipe.get('Facility', 'compressSpaces');
      case 'quantityBreak':       return LanguagePipe.get('Quantity break', 'compressSpaces');
      case 'numberUp':            return LanguagePipe.get('Number up', 'compressSpaces');
      default: return c;
    }
  }

  // ── Pivot drag-drop / swap ──────────────────────────────────────

  /** Drag a chip from one axis bin to another (or reorder within a bin). */
  onAxisDrop(event: CdkDragDrop<PropertyCharacteristic[]>): void {
    if (this.readOnly) { return; }
    const rows = this.effectiveRowAxes.slice();
    const cols = this.effectiveColAxes.slice();
    const src = event.previousContainer.id === this.rowBinId ? rows : cols;
    const dst = event.container.id === this.rowBinId ? rows : cols;
    if (src === dst) {
      moveItemInArray(src, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(src, dst, event.previousIndex, event.currentIndex);
    }
    this.axesLayoutChange.emit({rowAxes: rows, colAxes: cols});
  }

  /** "Swap rows ↔ columns" button. */
  swapAxes(): void {
    if (this.readOnly) { return; }
    this.axesLayoutChange.emit({
      rowAxes: this.effectiveColAxes.slice(),
      colAxes: this.effectiveRowAxes.slice()
    });
  }
}

// ── pure helpers ─────────────────────────────────────────────────────

/** Cartesian product of a list of value-lists. */
function cartesian(axes: string[][]): string[][] {
  if (axes.length === 0) { return [[]]; }
  let acc: string[][] = [[]];
  for (const values of axes) {
    const next: string[][] = [];
    for (const a of acc) {
      for (const v of values) { next.push([...a, v]); }
    }
    acc = next;
  }
  return acc;
}

/**
 * Build column-header rows. One row per axis level. Outer-level cells
 * use colspan = product of all inner axis lengths. The innermost row
 * emits one cell per column path.
 */
function buildColHeaderRows(axisValues: string[][],
                            axisChars: PropertyCharacteristic[],
                            describe: (c: PropertyCharacteristic, raw: string) => {label: string; tooltip: string}): HeaderCell[][] {
  const n = axisValues.length;
  if (n === 0) { return []; }
  const spans: number[] = new Array(n);
  spans[n - 1] = 1;
  for (let i = n - 2; i >= 0; i--) { spans[i] = spans[i + 1] * axisValues[i + 1].length; }
  const rows: HeaderCell[][] = [];
  for (let i = 0; i < n; i++) {
    let outerRepeat = 1;
    for (let j = 0; j < i; j++) { outerRepeat *= axisValues[j].length; }
    const cells: HeaderCell[] = [];
    const c = axisChars[i];
    for (let r = 0; r < outerRepeat; r++) {
      for (const v of axisValues[i]) {
        const d = c ? describe(c, v) : {label: v, tooltip: ''};
        cells.push({value: v, label: d.label, span: spans[i], axisLevel: i, characteristic: c, tooltip: d.tooltip});
      }
    }
    rows.push(cells);
  }
  return rows;
}

/**
 * Build per-body-row row-header cells. Returns an array of length
 * `totalRows`, where each entry is the list of header cells to emit
 * BEFORE the cell inputs on that row. Outer-axis cells are emitted
 * only on the first row of their rowspan group; subsequent rows under
 * the same outer cell omit it entirely (the rowspan on the prior row
 * covers them).
 *
 * For axes of sizes [a, b, c]:
 *  - row 0:                       [outer0=a0(span=b*c), inner1=b0(span=c), innermost=c0]
 *  - row 1:                       [                                      , innermost=c1]
 *  - row 2:                       [                   , inner1=b1(span=c), innermost=c0]
 *  - ...
 */
function buildRowHeaderCells(axisValues: string[][],
                             axisChars: PropertyCharacteristic[],
                             describe: (c: PropertyCharacteristic, raw: string) => {label: string; tooltip: string}): HeaderCell[][] {
  const n = axisValues.length;
  if (n === 0) { return []; }
  const spans: number[] = new Array(n);
  spans[n - 1] = 1;
  for (let i = n - 2; i >= 0; i--) { spans[i] = spans[i + 1] * axisValues[i + 1].length; }
  const totalRows = spans[0] * axisValues[0].length;
  const out: HeaderCell[][] = [];
  for (let r = 0; r < totalRows; r++) {
    const cells: HeaderCell[] = [];
    for (let i = 0; i < n; i++) {
      // The row "owns" an axis-i header cell when it is the FIRST row
      // in that cell's rowspan group: i.e. r % spans[i] === 0.
      if (r % spans[i] === 0) {
        const valueIdx = Math.floor(r / spans[i]) % axisValues[i].length;
        const v = axisValues[i][valueIdx];
        const c = axisChars[i];
        const d = c ? describe(c, v) : {label: v, tooltip: ''};
        cells.push({
          value: v,
          label: d.label,
          span: spans[i],
          axisLevel: i,
          characteristic: c,
          tooltip: d.tooltip
        });
      }
    }
    out.push(cells);
  }
  return out;
}
